import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["canRouteToAvailable", "channelType", "currentAssignee", "currentUserId", "genericChannelId", "loadingAssignee", "objectTypeId", "reassign", "routingSource", "teams", "shouldShowTooltip", "workspaceId", "usageTracker"];
import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import { useCallback, useMemo, useState } from 'react';
import debounce from 'react-utils/debounce';
import { ROUTING_RULE_TYPES } from '../../routing-rules/constants/RoutingTypes';
import { buildRoutingRulesFromType } from '../../routing-rules/utils/buildRoutingRules';
import { getChannelRoutingDistribution } from '../../routing-rules/utils/distribution';
import { ASSIGNED, SELF_ASSIGNED, TEAM_ASSIGNED, UNASSIGNED } from '../constants/AssignedStates';
import { useAssignmentOptionRenderer } from '../hooks/useAssignmentOptionRenderer';
import { useLoadOptions } from '../hooks/useLoadOptions';
import { getAgentId, getBotId } from '../operators/assignees';
import { currentAssigneeValueRenderer } from '../operators/currentAssigneeValueRenderer';
import { checkIsTeamAssignee } from '../utils/assignee-options';
import { getOpenedOwnerDropdown, getSearchedOwnerDropdown } from '../utils/event-handlers';
import AsyncUIAvatar from './AsyncUIAvatar';
import { jsx as _jsx } from "react/jsx-runtime";
const useAssignmentValueRenderer = (currentAssignee, loadingAssignee) => useMemo(() => currentAssigneeValueRenderer({
  AsyncUIAvatar,
  currentAssignee,
  loadingAssignee
}), [currentAssignee, loadingAssignee]);
const getUserAssignment = (id, currentUserId) => {
  let assignment = ASSIGNED;
  if (id === currentUserId) {
    assignment = SELF_ASSIGNED;
  }
  return assignment;
};
const gatherTeamIds = teams => {
  let ids = [];
  for (const team of teams) {
    ids.push(team.id);
    if (team.childTeams.length > 0) {
      ids = ids.concat(gatherTeamIds(team.childTeams));
    }
  }
  return ids;
};
const ReassignmentDropdown = _ref => {
  let {
      canRouteToAvailable,
      channelType,
      currentAssignee,
      currentUserId,
      genericChannelId,
      loadingAssignee,
      objectTypeId,
      reassign,
      routingSource,
      teams,
      shouldShowTooltip,
      workspaceId,
      usageTracker
    } = _ref,
    selectProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const currentAssigneeId =
  // @ts-expect-error Transmute's types think that its get function can't work with null/undefined but
  // if currentAssignee is either null or undefined, get will short circuit and return undefined
  getAgentId(currentAssignee) || getBotId(currentAssignee);
  const teamIds = useMemo(() => new Set(gatherTeamIds(teams)), [teams]);
  const loadOptions = useLoadOptions({
    currentUserId,
    objectTypeId,
    teams,
    workspaceId,
    routingSource,
    genericChannelId
  });
  const [open, setOpen] = useState(false);
  const trackUsage = useCallback(usageProperties => usageTracker && usageTracker.track(usageProperties.key, usageProperties.properties), [usageTracker]);
  const handleUnassign = useCallback(() => {
    setOpen(false);
    void reassign(UNASSIGNED);
  }, [reassign]);
  const optionRenderer = useAssignmentOptionRenderer({
    canRouteToAvailable,
    currentAssigneeId,
    currentUserId,
    handleUnassign,
    routingSource,
    shouldShowTooltip,
    workspaceId,
    showCapacityStatus: genericChannelId !== undefined // a null generic channel id signifies a channel-less ticket which defaults to EMAIL/OTHER capacity type.
  });
  const valueRenderer = useAssignmentValueRenderer(currentAssignee !== null && currentAssignee !== void 0 ? currentAssignee : null, loadingAssignee);
  const handleChange = useCallback(event => {
    const {
      value: option
    } = event.target;
    const id = option.value;
    const isTeamOption = checkIsTeamAssignee(option);
    if (isTeamOption && teamIds.has(id)) {
      void reassign(TEAM_ASSIGNED, {
        assignmentMethod: 'SYSTEM_AUTOMATIC',
        routingRuleDefinition: buildRoutingRulesFromType(ROUTING_RULE_TYPES.USERS_AND_TEAMS, {
          distribution: getChannelRoutingDistribution(channelType),
          teamIds: [id]
        }),
        shouldReassign: true
      });
    } else if (!isTeamOption && id !== currentAssigneeId) {
      // Only ASSIGNED | SELF_ASSIGNED
      const userAssignment = getUserAssignment(id, currentUserId);
      void reassign(userAssignment, option.responder);
    }
  }, [channelType, currentAssigneeId, currentUserId, reassign, teamIds]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTrackUsage = useCallback(debounce(() => trackUsage(getSearchedOwnerDropdown()), 1000), [trackUsage, getSearchedOwnerDropdown]);
  const handleOnInputChange = useCallback(() => {
    debouncedTrackUsage();
  }, [debouncedTrackUsage]);
  const handleOnOpenChange = useCallback(event => {
    const isOpen = event.target.value;
    setOpen(isOpen);
    if (isOpen) {
      trackUsage(getOpenedOwnerDropdown());
    }
  }, [setOpen, trackUsage]);
  const searchPlaceholder = useMemo(() => {
    return teams.length > 0 ? I18n.text('routing-ui.reassignment.searchPlaceholder.teamsAndUsers') : I18n.text('routing-ui.reassignment.searchPlaceholder.users');
  }, [teams]);
  return /*#__PURE__*/_jsx(UISelect, Object.assign({
    buttonUse: "link",
    cache: false,
    filterOptions: false,
    loadOptions: loadOptions,
    menuStyle: {
      maxHeight: 320
    },
    menuWidth: 360,
    minimumSearchCount: 0,
    onInputChange: handleOnInputChange,
    onOpenChange: handleOnOpenChange,
    onSelectedOptionChange: handleChange,
    open: open,
    optionRenderer: optionRenderer,
    placement: "bottom right",
    resultsClassName: "flat-result-options",
    searchable: true,
    searchPlaceholder: searchPlaceholder,
    value: currentAssigneeId || '',
    valueRenderer: valueRenderer
  }, selectProps));
};
ReassignmentDropdown.displayName = 'ReassignmentDropdown';
export default ReassignmentDropdown;